import { useEffect, useMemo, useState } from "react";
import { getBotbPassCompetitions } from "../botb-pass/subscription-service";
import { CompetitionsResponse } from "./types";
import { useManagementSubscriptionContext } from "./Context";
import { useUserHasAnyTicket } from "../header/hooks";
import dayjs from "dayjs";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";
import Skeleton from "react-loading-skeleton";

const COLORS = {
  "navigation-orange": {
    title: "text-nav-orange",
    bgBadge: "bg-nav-orange/20",
    textBadge: "text-nav-badge-orange",
  },
  "navigation-blue": {
    title: "text-nav-blue",
    bgBadge: "bg-nav-blue/20",
    textBadge: "text-nav-badge-blue",
  },
  "navigation-purple": {
    title: "text-nav-purple",
    bgBadge: "bg-nav-purple/20",
    textBadge: "text-nav-badge-purple",
  },
  "navigation-turquoise": {
    title: "text-nav-turquoise",
    bgBadge: "bg-nav-turquoise/20",
    textBadge: "text-nav-badge-turquoise",
  },
  "navigation-red": {
    title: "text-nav-red",
    bgBadge: "bg-nav-red/20",
    textBadge: "text-nav-badge-red",
  },
  "navigation-white": {
    title: "text-nav-white",
    bgBadge: "bg-nav-white/20",
    textBadge: "text-nav-badge-white",
  },
  "navigation-azure": {
    title: "text-nav-azure",
    bgBadge: "bg-nav-azure/20",
    textBadge: "text-nav-badge-azure",
  },
  "navigation-green": {
    title: "text-nav-green",
    bgBadge: "bg-nav-green/20",
    textBadge: "text-nav-badge-green",
  },
  "navigation-dark-fuchsia": {
    title: "text-nav-dark-fuchsia",
    bgBadge: "bg-nav-dark-fuchsia/20",
    textBadge: "text-nav-badge-dark-fuchsia",
  },
  "navigation-dark-blue": {
    title: "text-nav-dark-blue",
    bgBadge: "bg-nav-dark-blue/20",
    textBadge: "text-nav-badge-dark-blue",
  },
} as const;

const Step2 = () => {
  const { setBotbpassCompetitions, botbPassCompetitions, subscriptionPlan, onCancelSubscriptionModal, onNext } =
    useManagementSubscriptionContext();
  const [isCompetitionsLoading, setCompetitionsLoading] = useState(true);
  const shouldShowWalletZone = useUserHasAnyTicket();
  const redirectWalletUrl = "/account/my-wallet";
  const remainingTime = useMemo(() => {
    const diff = Math.abs(dayjs().diff(dayjs(subscriptionPlan.current_period_end * 1000), "milliseconds"));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) {
      if (days === 1) {
        return `${days} day`;
      }
      return `${days} days`;
    }
    if (hours === 1) {
      return `${hours} hour`;
    }
    return `${hours} hours`;
  }, [subscriptionPlan]);
  useEffect(() => {
    getBotbPassCompetitions()
      .then(res => res.json())
      .then((res: CompetitionsResponse) => {
        setBotbpassCompetitions(res);
        setCompetitionsLoading(false);
      })
      .catch(err => console.error(err));
  }, []);
  return (
    <div data-name={"modal-step-2"} className={"h-full grow flex flex-col"}>
      <div className={"scroll-wrapper grow relative mb-4"}>
        <div className={"absolute inset-0 size-full overflow-y-auto"}>
          <div className={"mb-10"}>
            <div
              className={
                "text-dark-1 text-center font-jost text-xl font-semibold leading-6.5 mb-1.5 max-lg:max-w-[276px] mx-auto lg:text-[28px] lg:leading-8"
              }>
              You have {remainingTime} left from current subscription!
            </div>
          </div>
          {isCompetitionsLoading ? (
            <>
              <Skeleton
                count={1}
                inline
                highlightColor="#fff"
                containerClassName="block w-full h-[216px] lg:h-[124px]"
                className="h-full"
              />
            </>
          ) : (
            <div
              className={`grid grid-cols-1 gap-4 lg:gap-8 ${botbPassCompetitions.lost && botbPassCompetitions.lost.length > 0 ? "lg:grid-cols-2" : "lg:w-1/2 lg:mx-auto"}`}>
              {botbPassCompetitions.played && botbPassCompetitions.played.length > 0 && (
                <div data-name={"in-play-prizes"} className={"lg:order-1"}>
                  <div
                    className={
                      "mb-3 text-secondary font-jost text-sm font-semibold leading-5 self-stretch lg:text-base lg:leading-5.5"
                    }>
                    You’re in play for these prizes:
                  </div>
                  <div>
                    {botbPassCompetitions.played.map(playedCompetition => (
                      <div
                        key={playedCompetition.CompetitionId}
                        className={
                          "flex items-center gap-3 w-full mb-2 lg:mb-4 last:mb-0 pb-2 max-lg:last:pb-0 border-b lg:border-0 border-b-black/8 last:border-b-0 bg-white lg:shadow-[0_1.5px_8px_0_rgba(0,0,0,0.08)] lg:pl-1 lg:pr-4 lg:py-1 rounded-lg"
                        }>
                        <img
                          src={playedCompetition.ImageUrl}
                          alt={String(playedCompetition.CompetitionId)}
                          width={32}
                          height={32}
                          className={"size-8 lg:w-[97px] lg:h-[60px] object-cover rounded shrink-0"}
                        />
                        <div className={"flex flex-col items-start gap-1 lg:gap-2"}>
                          <div
                            className={`${COLORS[playedCompetition.NavigationColour as keyof typeof COLORS]?.title} font-jost text-sm font-bold leading-5 lg:text-base lg:font-semibold lg:leading-5.5`}>
                            {playedCompetition.Title}
                          </div>
                          <div
                            className={`${COLORS[playedCompetition.NavigationColour as keyof typeof COLORS]?.bgBadge} ${COLORS[playedCompetition.NavigationColour as keyof typeof COLORS]?.textBadge} flex justify-center items-center gap-2.5 rounded px-1 py-0.5 lg:px-1.5 lg:py-[3px] font-jost text-[10px] font-medium leading-3 uppercase lg:text-xs lg:leading-4`}>
                            ENDS {playedCompetition.EndDate.toUpperCase()}
                          </div>
                        </div>
                        <a
                          href={playedCompetition.PageUrl}
                          title={"Details"}
                          className={
                            "ml-auto text-primary text-right font-jost text-sm font-medium leading-5 !underline"
                          }>
                          Details
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!shouldShowWalletZone && (
                <div
                  data-name={"wallet"}
                  className={`flex justify-between items-center bg-gray-4 px-2 py-3 rounded-lg lg:order-3 lg:shadow-[0_1.5px_8px_0_rgba(0,0,0,0.20)] lg:px-3 lg:py-1 ${botbPassCompetitions.lost && botbPassCompetitions.lost.length > 0 ? "lg:col-span-2" : ""}`}>
                  <div className={"flex items-center gap-2"}>
                    <img
                      src={"/assets/images/subscriptions/wallet-tickets.webp"}
                      alt={"wallet tickets"}
                      width={48}
                      height={48}
                      className={"size-12 lg:size-14 aspect-square shrink-0"}
                    />
                    <div
                      className={
                        "text-dark-1 lg:text-secondary font-jost text-[13px] font-medium leading-4.5 lg:text-base lg:font-semibold lg:leading-5.5"
                      }>
                      You have <br className={"lg:hidden"} />
                      unused tickets!
                    </div>
                  </div>
                  <a
                    href={redirectWalletUrl}
                    title={"View wallet"}
                    className={"ml-auto theme-btn theme-btn-primary-outline theme-btn-sm"}>
                    View wallet
                    <ChevronsRightIcon className={"size-4 shrink-0 text-primary"} />
                  </a>
                </div>
              )}

              {botbPassCompetitions.lost && botbPassCompetitions.lost.length > 0 && (
                <div data-name={"lost-prizes"} className={"lg:order-2"}>
                  <div
                    className={
                      "mb-3 text-secondary font-jost text-sm font-semibold leading-5 self-stretch lg:text-base lg:leading-5.5"
                    }>
                    Entries that will end with cancellation*:
                  </div>
                  <div>
                    {botbPassCompetitions.lost.map(lostCompetition => (
                      <div
                        key={lostCompetition.CompetitionId}
                        className={
                          "flex items-center gap-3 w-full mb-2 last:mb-0 pb-2 last:pb-0 border-b border-b-black/8 last:border-b-0 bg-white lg:shadow-[0_1.5px_8px_0_rgba(0,0,0,0.08)] lg:pl-1 lg:pr-4 lg:py-1 rounded-lg"
                        }>
                        <img
                          src={lostCompetition.ImageUrl}
                          alt={String(lostCompetition.CompetitionId)}
                          width={32}
                          height={32}
                          className={"size-8 lg:w-[97px] lg:h-[60px] object-cover rounded shrink-0"}
                        />
                        <div className={"flex flex-col items-start gap-1 lg:gap-2"}>
                          <div
                            className={`${COLORS[lostCompetition.NavigationColour as keyof typeof COLORS]?.title} font-jost text-sm font-bold leading-5 lg:text-base lg:font-semibold lg:leading-5.5`}>
                            {lostCompetition.Title}
                          </div>
                          <div
                            className={`${COLORS[lostCompetition.NavigationColour as keyof typeof COLORS]?.bgBadge} ${COLORS[lostCompetition.NavigationColour as keyof typeof COLORS]?.textBadge} flex justify-center items-center gap-2.5 rounded px-1 py-0.5 lg:px-1.5 lg:py-[3px] font-jost text-[10px] font-medium leading-3 uppercase lg:text-xs lg:leading-4`}>
                            ENDS {lostCompetition.EndDate.toUpperCase()}
                          </div>
                        </div>
                        <a
                          href={lostCompetition.PageUrl}
                          title={"Details"}
                          className={
                            "ml-auto text-primary text-right font-jost text-sm font-medium leading-5 !underline"
                          }>
                          Details
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className={"mt-2 text-gray-5 font-jost text-xs font-normal leading-4"}>
                    *Tickets made available to these competitions are part of your next subscription period’s package.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={"flex gap-2 lg:justify-end"}>
        <button onClick={onNext} className={"theme-btn theme-btn-md uppercase max-lg:w-full text-primary"}>
          Cancel anyway
        </button>
        <button
          onClick={onCancelSubscriptionModal}
          className={"theme-btn theme-btn-primary theme-btn-md uppercase max-lg:w-full"}>
          Keep subscription
        </button>
      </div>
    </div>
  );
};

export default Step2;
